import React, { useState } from 'react';

const EMPTY_ARTICLE = { title: '', authors: '', year: '', abstract: '', limitations: '' };

export default function Step2_ArticleInput({ articles, onChange, onNext, onBack }) {
  const [expandedIdx, setExpandedIdx] = useState(0);

  const updateArticle = (idx, field, val) => {
    const updated = articles.map((a, i) =>
      i === idx ? { ...a, [field]: val } : a
    );
    onChange(updated);
  };

  const addArticle = () => {
    if (articles.length < 15) {
      onChange([...articles, { ...EMPTY_ARTICLE, id: Date.now() }]);
      setExpandedIdx(articles.length);
    }
  };

  const removeArticle = (idx) => {
    if (articles.length > 10) {
      onChange(articles.filter((_, i) => i !== idx));
    }
  };

  const validArticles = articles.filter(
    (a) => a.title.trim() && a.authors.trim() && a.year.trim() && a.abstract.trim()
  );

  const canProceed = validArticles.length >= 10;

  return (
    <div className="gf-step">
      <div className="gf-step-header">
        <span className="gf-step-badge">Phase 2</span>
        <h2>Literature Input</h2>
        <p>
          Enter 10–15 research article abstracts with citations. Each abstract should include
          the title, author(s), year, and abstract text. The limitations/future research field
          is optional but strongly recommended for better gap detection.
        </p>
        <div className="gf-progress-bar-wrap">
          <div
            className="gf-progress-bar-fill"
            style={{ width: `${Math.min(100, (validArticles.length / 15) * 100)}%` }}
          />
        </div>
        <p className="gf-hint">{validArticles.length}/15 complete · minimum 10 required</p>
      </div>

      <div className="gf-articles-list">
        {articles.map((article, idx) => (
          <div
            key={article.id || idx}
            className={`gf-article-card ${expandedIdx === idx ? 'expanded' : ''}`}
          >
            <div className="gf-article-header" onClick={() => setExpandedIdx(expandedIdx === idx ? -1 : idx)}>
              <span className="gf-article-num">Article {idx + 1}</span>
              <span className="gf-article-preview">
                {article.title || <em>Untitled</em>}
                {article.authors && ` — ${article.authors}`}
                {article.year && ` (${article.year})`}
              </span>
              <div className="gf-article-header-actions">
                {validArticles.includes(article) && <span className="gf-badge-complete">✓</span>}
                <span className="gf-expand-icon">{expandedIdx === idx ? '▲' : '▼'}</span>
              </div>
            </div>

            {expandedIdx === idx && (
              <div className="gf-article-fields">
                <div className="gf-form-grid">
                  <div className="gf-field gf-field-full">
                    <label>Article Title *</label>
                    <input
                      type="text"
                      placeholder="Full title of the article"
                      value={article.title}
                      onChange={(e) => updateArticle(idx, 'title', e.target.value)}
                    />
                  </div>
                  <div className="gf-field">
                    <label>Author(s) *</label>
                    <input
                      type="text"
                      placeholder="e.g. Smith, J. & Jones, A."
                      value={article.authors}
                      onChange={(e) => updateArticle(idx, 'authors', e.target.value)}
                    />
                  </div>
                  <div className="gf-field">
                    <label>Year *</label>
                    <input
                      type="number"
                      placeholder="e.g. 2023"
                      min="1990"
                      max="2030"
                      value={article.year}
                      onChange={(e) => updateArticle(idx, 'year', e.target.value)}
                    />
                  </div>
                  <div className="gf-field gf-field-full">
                    <label>Abstract * <span className="gf-char-count">{(article.abstract || '').length} chars</span></label>
                    <textarea
                      rows={5}
                      placeholder="Paste the full abstract here..."
                      value={article.abstract}
                      onChange={(e) => updateArticle(idx, 'abstract', e.target.value)}
                    />
                  </div>
                  <div className="gf-field gf-field-full">
                    <label>Limitations / Future Research Section <span className="gf-optional">(optional but recommended)</span></label>
                    <textarea
                      rows={3}
                      placeholder="Paste the limitations or future research section if available..."
                      value={article.limitations}
                      onChange={(e) => updateArticle(idx, 'limitations', e.target.value)}
                    />
                  </div>
                </div>
                {articles.length > 10 && (
                  <button className="gf-btn gf-btn-danger gf-btn-sm" onClick={() => removeArticle(idx)}>
                    Remove Article
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {articles.length < 15 && (
        <button className="gf-btn gf-btn-outline" onClick={addArticle}>
          + Add Another Article ({articles.length}/15)
        </button>
      )}

      {!canProceed && (
        <p className="gf-hint gf-warn">
          Please complete at least 10 articles before proceeding.
        </p>
      )}

      <div className="gf-nav">
        <button className="gf-btn gf-btn-secondary" onClick={onBack}>← Back</button>
        <button className="gf-btn gf-btn-primary" onClick={onNext} disabled={!canProceed}>
          Run Gap Analysis →
        </button>
      </div>
    </div>
  );
}
